/**
 * OneSignal Client Service
 * Handles OneSignal subscription and notification actions
 * Requirements.md E: Browser-Closed Notifications
 *
 * Note: OneSignal is initialized via CDN script in index.html
 * This service provides helper functions to interact with the global OneSignal object
 */

import { apiRequest } from '@/lib/queryClient';

// Declare global OneSignal type
declare global {
  interface Window {
    OneSignal: any;
    OneSignalDeferred: any[];
  }
}

let isInitialized = false;

/**
 * Detect device type and browser for platform-specific notification handling
 * Returns: 'safari-mac', 'safari-ios', 'chrome-ios', 'chrome-desktop', 'chrome-android', 'firefox', 'edge', 'web'
 */
function detectDeviceType(): string {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() || '';

  // Detect iOS (iPhone, iPad, iPod)
  const isIOS =
    /iphone|ipad|ipod/.test(userAgent) || (platform === 'macintel' && navigator.maxTouchPoints > 1); // iPad on iOS 13+

  // Detect Mac
  const isMac = /macintosh|mac os x/.test(userAgent) && !isIOS;

  // Detect Safari
  const isSafari =
    /safari/.test(userAgent) && !/chrome|chromium|crios|fxios|edgios/.test(userAgent);

  // Detect Chrome on iOS (uses WebKit, same limitations as Safari)
  const isChromeIOS = /crios/.test(userAgent);

  // Detect Android
  const isAndroid = /android/.test(userAgent);

  // Detect Chrome on Android or Desktop
  const isChrome = /chrome|chromium/.test(userAgent) && !/edg/.test(userAgent);

  // Detect Firefox
  const isFirefox = /firefox/.test(userAgent);

  // Detect Edge
  const isEdge = /edg/.test(userAgent);

  // Return specific device type
  if (isSafari && isIOS) return 'safari-ios';
  if (isSafari && isMac) return 'safari-mac';
  if (isChromeIOS) return 'chrome-ios';
  if (isChrome && isAndroid) return 'chrome-android';
  if (isChrome) return 'chrome-desktop';
  if (isFirefox) return 'firefox';
  if (isEdge) return 'edge';

  return 'web'; // Fallback
}

/**
 * Check if the current browser/platform supports notification action buttons
 */
export function supportsNotificationButtons(): boolean {
  const deviceType = detectDeviceType();

  // Safari (Mac & iOS) and Chrome on iOS do NOT support action buttons
  const unsupportedPlatforms = ['safari-mac', 'safari-ios', 'chrome-ios'];

  return !unsupportedPlatforms.includes(deviceType);
}

/**
 * Wait for OneSignal to be ready
 * OneSignal is loaded via CDN and initialized in index.html
 */
function waitForOneSignal(): Promise<any> {
  return new Promise((resolve) => {
    if (window.OneSignal) {
      resolve(window.OneSignal);
    } else {
      // Wait for OneSignal to load
      const checkInterval = setInterval(() => {
        if (window.OneSignal) {
          clearInterval(checkInterval);
          resolve(window.OneSignal);
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        console.error('❌ OneSignal failed to load within 10 seconds');
        resolve(null);
      }, 10000);
    }
  });
}

/**
 * Initialize OneSignal event handlers
 * OneSignal SDK is already initialized via CDN in index.html
 * This function just sets up event handlers
 */
export async function initializeOneSignal(): Promise<void> {
  if (isInitialized) {
    console.log('OneSignal handlers already initialized');
    return;
  }

  try {
    const OneSignal = await waitForOneSignal();

    if (!OneSignal) {
      console.error('❌ OneSignal SDK not loaded');
      return;
    }

    console.log('✅ OneSignal SDK detected, setting up event handlers');

    // Set up notification event handlers
    setupNotificationHandlers();

    isInitialized = true;
  } catch (error) {
    console.error('❌ Failed to initialize OneSignal handlers:', error);
    throw error;
  }
}

/**
 * Subscribe user to push notifications
 * Saves the player ID to the server
 *
 * IMPORTANT: This function now uses OneSignal.Slidedown.promptPush() instead of
 * OneSignal.Notifications.requestPermission() to properly trigger the permission flow.
 *
 * The promptPush() method:
 * 1. Shows OneSignal's slidedown prompt (if configured)
 * 2. Then shows the native browser permission prompt
 * 3. Creates the subscription automatically
 */
export async function subscribeToNotifications(userId: string): Promise<void> {
  try {
    const OneSignal = await waitForOneSignal();

    if (!OneSignal) {
      console.error('❌ OneSignal SDK not loaded');
      return;
    }

    // CRITICAL: Set external user ID FIRST before requesting permission
    // This ensures the subscription is linked to the correct user
    // The subscription will persist even after logout/browser restart
    await OneSignal.login(userId);
    console.log('✅ User logged in to OneSignal:', userId);

    // Check if already subscribed
    const currentPermission = await OneSignal.Notifications.permission;
    const isOptedIn = await OneSignal.User.PushSubscription.optedIn;

    if (currentPermission && isOptedIn) {
      console.log('✅ User already subscribed to notifications');

      // CRITICAL: Always re-save player ID to server on login
      // This ensures the subscription is active even after browser restart
      const playerId = await OneSignal.User.PushSubscription.id;
      if (playerId) {
        const deviceType = detectDeviceType();
        try {
          await apiRequest('POST', '/api/onesignal/subscribe', {
            playerId,
            deviceType,
          });
          console.log('✅ Player ID re-saved to server:', playerId, 'Device:', deviceType);
        } catch (error) {
          console.error('❌ Failed to save player ID to server:', error);
          // Don't throw - subscription still works locally
        }
      }
      return;
    }

    // Trigger the slidedown prompt which will then show the native browser prompt
    // This is the correct way to request permission in OneSignal Web SDK v16
    console.log('📬 Showing notification permission prompt...');
    await OneSignal.Slidedown.promptPush({ force: true });

    // Wait a bit for the permission flow to complete
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // Check if permission was granted
    const permission = await OneSignal.Notifications.permission;

    if (!permission) {
      console.log('❌ Notification permission denied or dismissed');
      return;
    }

    console.log('✅ Notification permission granted');

    // Get OneSignal player ID
    const playerId = await OneSignal.User.PushSubscription.id;

    if (!playerId) {
      console.error('❌ Failed to get OneSignal player ID');
      return;
    }

    console.log('OneSignal Player ID:', playerId);

    // Save player ID to server with detected device type
    const deviceType = detectDeviceType();
    const response = await apiRequest('POST', '/api/onesignal/subscribe', {
      playerId,
      deviceType,
    });

    const data = await response.json();
    console.log('✅ Subscribed to OneSignal:', data, 'Device:', deviceType);

    // Log whether this device supports action buttons
    const hasButtonSupport = supportsNotificationButtons();
    console.log(
      `📱 Device: ${deviceType}, Action Buttons: ${hasButtonSupport ? '✅ Supported' : '❌ Not Supported'}`
    );
  } catch (error) {
    console.error('❌ Failed to subscribe to notifications:', error);
    throw error;
  }
}

/**
 * Unsubscribe user from push notifications
 */
export async function unsubscribeFromNotifications(): Promise<void> {
  try {
    const OneSignal = await waitForOneSignal();

    if (!OneSignal) {
      console.error('❌ OneSignal SDK not loaded');
      return;
    }

    const playerId = await OneSignal.User.PushSubscription.id;

    if (!playerId) {
      console.warn('No player ID found');
      return;
    }

    // Unsubscribe from OneSignal
    await OneSignal.User.PushSubscription.optOut();

    // Update server
    await apiRequest('POST', '/api/onesignal/unsubscribe', {
      playerId,
    });

    console.log('✅ Unsubscribed from notifications');
  } catch (error) {
    console.error('❌ Failed to unsubscribe from notifications:', error);
    throw error;
  }
}

/**
 * Check if user is subscribed to notifications
 */
export async function isSubscribed(): Promise<boolean> {
  try {
    const OneSignal = await waitForOneSignal();

    if (!OneSignal) {
      return false;
    }

    const permission = await OneSignal.Notifications.permission;
    const isOptedIn = await OneSignal.User.PushSubscription.optedIn;

    return permission && isOptedIn;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    return false;
  }
}

/**
 * Set up notification event handlers
 */
async function setupNotificationHandlers(): Promise<void> {
  const OneSignal = await waitForOneSignal();

  if (!OneSignal) {
    console.error('❌ Cannot set up handlers - OneSignal SDK not loaded');
    return;
  }

  // Handle notification click
  OneSignal.Notifications.addEventListener('click', async (event: any) => {
    try {
      console.log('🔔 OneSignal notification clicked (client-side):', event);
      console.log('🔍 Event object:', event);
      console.log('🔍 Notification:', event.notification);
      console.log('🔍 Additional data:', event.notification?.additionalData);
      console.log('🔍 Action ID:', event.action);

      const data = event.notification?.additionalData || event.notification?.data;
      const actionId = event.action; // Button ID: 'log_150ml', 'log_250ml', 'custom_amount', or undefined

      console.log('🔍 Parsed data:', data);
      console.log('🔍 Data type:', data?.type);

      if (data?.type === 'hydration_log' || data?.type === 'hydration_reminder') {
        console.log('💧 Hydration notification clicked:', data, 'Action:', actionId);

        // Handle button clicks - log water directly via API
        if (actionId === 'log_150ml') {
          console.log('✅ 150ml button clicked - logging water');
          event.preventDefault(); // Prevent default navigation

          try {
            await logWaterFromNotificationAction(150);
            console.log('✅ 150ml logged successfully');

            window.dispatchEvent(
              new CustomEvent('waterLogged', {
                detail: {
                  volume: 150,
                  source: 'notification',
                },
              })
            );
          } catch (error) {
            console.error('❌ Failed to log 150ml:', error);
          }

          return;
        } else if (actionId === 'log_250ml') {
          console.log('✅ 250ml button clicked - logging water');
          event.preventDefault(); // Prevent default navigation

          try {
            await logWaterFromNotificationAction(250);
            console.log('✅ 250ml logged successfully');

            window.dispatchEvent(
              new CustomEvent('waterLogged', {
                detail: {
                  volume: 250,
                  source: 'notification',
                },
              })
            );
          } catch (error) {
            console.error('❌ Failed to log 250ml:', error);
          }
        } else if (actionId === 'custom_amount') {
          console.log('📱 Opening app for custom amount input');
          if (!window.location.pathname.startsWith('/dashboard')) {
            window.location.assign('/dashboard?action=log-water-custom');
          }
        } else {
          // Body click: OneSignal already opened this tab to the launch URL.
          // Don't force a reload — that remounts React and previously hit a
          // NewsCarousel crash ("Something went wrong") on Mac and mobile.
          console.log('📱 Notification body clicked — staying on Mainstage');
          if (window.location.pathname !== '/dashboard') {
            window.location.assign('/dashboard');
          }
        }
      }
    } catch (error) {
      console.error('❌ Notification click handler failed:', error);
    }
  });

  // Handle notification received (when app is open) — pull latest hydration state
  // (e.g. drink logged on another device → new/updated notif while Mainstage is open)
  OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event: any) => {
    console.log('Notification received in foreground:', event);
    window.dispatchEvent(
      new CustomEvent('hydrationRemoteUpdate', {
        detail: { source: 'onesignal-foreground' },
      })
    );
  });

  // Handle notification permission change
  OneSignal.Notifications.addEventListener('permissionChange', (permission: boolean) => {
    console.log('Notification permission changed:', permission);
  });
}

/**
 * Log water from notification action
 * This is called when user clicks "Log Water" button in notification
 */
export async function logWaterFromNotificationAction(volume: number): Promise<void> {
  try {
    const response = await apiRequest('POST', '/api/onesignal/log-from-notification', {
      volume,
    });

    const data = await response.json();
    console.log('✅ Water logged from notification:', data);

    return data;
  } catch (error) {
    console.error('❌ Failed to log water from notification:', error);
    throw error;
  }
}

/**
 * Get notification tracking data
 */
export async function getNotificationTracking(date?: string): Promise<any> {
  try {
    const queryParams = date ? `?date=${date}` : '';
    const response = await apiRequest('GET', `/api/onesignal/tracking${queryParams}`);
    const data = await response.json();

    return data;
  } catch (error) {
    console.error('Error fetching notification tracking:', error);
    throw error;
  }
}

/**
 * Check if OneSignal is initialized
 */
export function isOneSignalInitialized(): boolean {
  return isInitialized;
}

/**
 * Get OneSignal player ID
 */
export async function getPlayerId(): Promise<string | null> {
  try {
    const OneSignal = await waitForOneSignal();

    if (!OneSignal) {
      return null;
    }

    const playerId = await OneSignal.User.PushSubscription.id;
    return playerId;
  } catch (error) {
    console.error('Error getting player ID:', error);
    return null;
  }
}
